// The top title bar: the brand lockup, the application menu bar (File / Edit /
// View / …), a center command affordance, and the primary actions (Compile,
// theme). Compile is the single build action: it certifies every spend path as
// its funding gate, so a separate "certify" button would only duplicate that and
// confuse the verdict. Flat, real icons (lucide). Parameterless: binds to store.

import { Play, Palette, Search, Loader2, Check } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useIde } from "../store";
import { MenuBar } from "./MenuBar";
import { themeDef, nextTheme } from "../theme";
import "./shell.css";

/** The Seal mark: the exact welcome-screen mascot, badge-less with a soft drop
 *  shadow -- a quiet brand lockup. The reacting companion lives in the editor. */
function BrandMark() {
  return (
    <svg className="tb-mark" width="32" height="25" viewBox="14 16 88 68" fill="none" aria-hidden="true">
      <ellipse cx="22" cy="72" rx="7" ry="4.6" fill="#fbf7ef" transform="rotate(-26 22 72)" />
      <path d="M18 58 C18 32 38 18 60 18 C82 18 102 32 102 58 C102 75 89 83 73 83.5 C65 84 55 84 47 83.5 C31 83 18 75 18 58 Z" fill="#fbf7ef" />
      <circle cx="43" cy="60" r="4.6" fill="#f0a07f" opacity="0.6" />
      <circle cx="77" cy="60" r="4.6" fill="#f0a07f" opacity="0.6" />
      <g className="seal-eye">
        <ellipse cx="51.5" cy="53" rx="3.5" ry="4.5" fill="#5f4030" />
        <circle cx="52.7" cy="51.4" r="1.15" fill="#fff" opacity="0.9" />
      </g>
      <g className="seal-eye">
        <ellipse cx="68.5" cy="53" rx="3.5" ry="4.5" fill="#5f4030" />
        <circle cx="69.7" cy="51.4" r="1.15" fill="#fff" opacity="0.9" />
      </g>
      <ellipse cx="60" cy="60.5" rx="3.3" ry="2.4" fill="#6b4a36" />
      <path d="M60 62.9 L60 66" stroke="#6b4a36" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M60 66 Q55.6 70.4 51.8 67.8" stroke="#6b4a36" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M60 66 Q64.4 70.4 68.2 67.8" stroke="#6b4a36" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M48 59.5 Q40 58 32.5 57.8" stroke="#6b4a36" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M48 63.5 Q40.5 65 33.5 67.5" stroke="#6b4a36" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M72 59.5 Q80 58 87.5 57.8" stroke="#6b4a36" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M72 63.5 Q79.5 65 86.5 67.5" stroke="#6b4a36" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

export function TitleBar() {
  const theme = useIde((s) => s.prefs.theme);
  const status = useIde((s) => s.status);
  const setTheme = useIde((s) => s.setTheme);
  const setCommandPalette = useIde((s) => s.setCommandPalette);
  const runCompile = useIde((s) => s.runCompile);

  const compiling = status === "compiling";

  // A successful build is near-instant, so the spinner alone reads as a flicker.
  // Flash a tick for a beat when a compile transitions compiling -> ok, then fall
  // back to the play icon. Failures don't tick (the error surfaces in Problems).
  const [showDone, setShowDone] = useState(false);
  const prevStatus = useRef(status);
  useEffect(() => {
    const succeeded = prevStatus.current === "compiling" && status === "ok";
    prevStatus.current = status;
    if (succeeded) {
      setShowDone(true);
      const t = setTimeout(() => setShowDone(false), 1100);
      return () => clearTimeout(t);
    }
    if (status === "compiling") setShowDone(false); // a new build clears a lingering tick
  }, [status]);
  const runPhase = compiling ? "compiling" : showDone ? "done" : "idle";

  const upcoming = themeDef(nextTheme(theme));
  const isMac =
    typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
  const mod = isMac ? "⌘" : "Ctrl";

  return (
    <header className="tb" role="banner">
      <div className="tb-left">
        <div className="tb-brand">
          <BrandMark />
          <span className="tb-wordmark">Seal</span>
        </div>
        <MenuBar />
      </div>

      <div className="tb-center">
        <button
          type="button"
          className="tb-command"
          onClick={() => setCommandPalette(true)}
          title="Show all commands"
          aria-label="Open command palette"
        >
          <Search size={13} strokeWidth={2} />
          <span className="tb-command-text">Search commands…</span>
          <kbd className="tb-kbd">{`⇧${mod}P`}</kbd>
        </button>
      </div>

      <div className="tb-actions">
        <button
          type="button"
          className="tb-run"
          data-phase={runPhase}
          onClick={() => runCompile("fund")}
          disabled={compiling}
          title={`Certify every spend path and compile to a funding address (${mod}⏎)`}
        >
          {/* All three icons live in one fixed-size slot and cross-fade; the label
              is constant. Nothing reflows between phases, so a fast build reads as
              spinner -> tick rather than a flicker. */}
          <span className="tb-run-ico" aria-hidden="true">
            <Play className="tb-run-play" size={12} strokeWidth={0} fill="currentColor" />
            <Loader2 className="tb-run-spin spin" size={13} />
            <Check className="tb-run-done" size={14} strokeWidth={3} />
          </span>
          <span className="tb-run-label">Compile</span>
        </button>
        <button
          type="button"
          className="tb-icon-btn"
          onClick={() => setTheme(nextTheme(theme))}
          title={`Theme: ${themeDef(theme).label}. Click for ${upcoming.label}`}
          aria-label="Cycle theme"
        >
          <Palette size={16} strokeWidth={1.85} />
        </button>
      </div>
    </header>
  );
}
